import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";

import { AppMenuComponent } from "../components/app-menu/app-menu.component";
import { IVisitation, NotHome, Visitation } from "../interfaces/api-heatbrain";
import { ApiHeatbrainService } from "./api-heatbrain/api-heatbrain.service";
import { CONTENT_TYPE } from "./api-heatbrain/request";

@Injectable({
  providedIn: "root",
})
export class EnergieklusVisitationService {
  constructor(
    private apiHeatbrainService: ApiHeatbrainService,
    public appMenu: AppMenuComponent,
  ) {}

  public getEnergieklusVisitationsByActionId(
    action_id: string,
    admin_code: string | null = null,
  ): Promise<Visitation[]> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations2"]);
    req.setQueryParameters({
      action_id: action_id,
      project_type: this.appMenu.currentApp,
      admin_code: admin_code,
    });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.get_public<Visitation[]>(req, console.log);
  }

  public get_energieklus_visitation(
    visitation_id: string,
    code: string | null = null,
    actionId: string | null = null,
  ): Promise<Visitation> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations", visitation_id]);
    req.setHeaders(CONTENT_TYPE.JSON);
    if (code && actionId) {
      return this.apiHeatbrainService.get_basic<Visitation>(
        req,
        actionId,
        code,
        this.alert_on_error,
      );
    } else {
      return this.apiHeatbrainService.get<Visitation>(req, this.alert_on_error);
    }
  }

  public delete_energieklus_visitation(
    visitation_id: string,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "timeslot",
    ]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.delete(req, this.alert_on_error);
  }

  public cancel_energieklus_visitation(
    visitation_id: string,
    reason: string = "sobolt",
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations", visitation_id]);
    req.setQueryParameters({
      reason: reason,
    });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.delete_public(req, this.alert_on_error);
  }

  public get_energieklus_visitation_times(
    visitation_id,
  ): Promise<{ [key: string]: any }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "times",
    ]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.get(req, this.alert_on_error);
  }

  public get_visitee_email(visitation_id): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "email",
    ]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.get(req, this.alert_on_error);
  }

  public set_energieklus_visitation_start_time(
    visitation_id,
    started_at: Date,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "started_at",
    ]);
    const seconds = Math.round(started_at.getTime() / 1000); // - (started_at.getTimezoneOffset() * 60);
    req.setQueryParameters({ time: seconds });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }

  public set_visitor_email(visitation_id, email): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "visitor_email",
    ]);
    req.setQueryParameters({ email: email });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }

  public set_energieklus_visitation_finish_time(
    visitation_id,
    finished_at: Date,
  ): Promise<{ [key: string]: any }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "finished_at",
    ]);
    const seconds = Math.round(finished_at.getTime() / 1000); // - (finished_at.getTimezoneOffset() * 60);
    req.setQueryParameters({ time: seconds });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }

  public submit_energieklus_visitation_registration(
    visitation_id: string,
    regForm: FormGroup,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations", visitation_id]);
    let obj = {};
    obj["phone_number"] = regForm.controls["phoneNumber"].value;
    obj["opt_code"] = regForm.controls["accessCode"].value;
    obj["postal_code"] = regForm.controls["postalCode"].value;
    obj["email"] = regForm.controls["email"].value;
    obj["project_id"] = regForm.controls["project_id"].value;
    obj["project_type"] = this.appMenu.currentApp;
    req.setRequestBody(obj);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.post_public(req, this.alert_on_error);
  }

  public submit_energieklus_visitation_registration_legacy(
    visitation_id: string,
    reg: any,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations", visitation_id]);
    reg["project_type"] = this.appMenu.currentApp;
    req.setRequestBody(reg);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.post_public(req, this.alert_on_error);
  }

  public submit_energieklus_visitation_registration_lean(
    visitation_id: string,
    visitationInfo: any,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations_lean",
      visitation_id,
    ]);
    let obj = {};
    obj["address_id"] = visitationInfo.address_id;
    obj["name"] = visitationInfo.name;
    obj["project_id"] = visitationInfo.project_id;
    obj["project_type"] = this.appMenu.currentApp;
    if (!visitationInfo.email) {
      visitationInfo.email = "";
    }
    if (!visitationInfo.phone_number) {
      visitationInfo.phone_number = "";
    }
    obj["email"] = visitationInfo.email;
    obj["phone_number"] = visitationInfo.phone_number;
    req.setRequestBody(obj);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.post_public(req, this.alert_on_error);
  }

  /**
   * Updates the phone number for a specific visitation.
   * @param visitation_id The ID of the visitation to update.
   * @param phone_number The new phone number.
   * @returns A promise that resolves with the API response.
   */
  public set_visitee_phone_number(
    visitation_id: string,
    phone_number: string,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "phone",
    ]);
    req.setQueryParameters({ phone_number: phone_number });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }

  /**
   * Updates the timeslot for a specific visitation.
   * @param visitation_id The ID of the visitation to update.
   * @param timeslot The new timeslot.
   * @returns A promise that resolves with the API response.
   */
  public set_visitation_timeslot(
    visitation_id: string,
    timeslot: string | null,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "timeslot",
    ]);

    if (timeslot) {
      req.setQueryParameters({ timeslot: new Date(timeslot).toISOString() });
    }
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }
  /**
   * Triggers the sending of a confirmation email for a specific visitation.
   * @param visitation_id The ID of the visitation.
   * @returns A promise that resolves with the API response.
   */
  public send_confirmation_email(
    visitation_id: string,
  ): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "send_confirmation_email",
    ]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.post(req, this.alert_on_error);
  }

  public set_visitee_email(visitation_id, email): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "visitations",
      visitation_id,
      "email",
    ]);
    req.setQueryParameters({ email: email });
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put(req, this.alert_on_error);
  }

  public update_visitation(visitation: Visitation): Promise<{ [key: string]: string }> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", "visitations"]);
    req.setRequestBody(visitation);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.put<any>(req, () => {});
  }

  public set_owner_not_home(address_id: string): Promise<NotHome> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "addresses", address_id, "not-home"]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.post<NotHome>(req, this.alert_on_error);
  }

  public alert_on_error(error: HttpErrorResponse): void {
    alert(`${error.error.detail}`);
  }

  public get_visitations_for_address(addressId: string): Promise<IVisitation[]> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["addresses", addressId, "visitations"]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.get<IVisitation[]>(req, console.error);
  }

  public check_admin_code_and_klusser_guard(adminCode: string): Promise<any> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters([
      "energieklus",
      "projects",
      "check_admin_code_and_klusser_guard",
      adminCode,
    ]);
    req.setHeaders(CONTENT_TYPE.JSON);
    return this.apiHeatbrainService.get<any>(req, console.error);
  }

  public create_visitation(
    projectId: string,
    visitee_id: string,
    visitee_name: string,
    visitee_email: string,
    visitee_phone: string,
    timeslot: Date,
    send_confirmation_emails: boolean,
  ): Promise<IVisitation> {
    let req = this.apiHeatbrainService.requestFactory();
    req.setUrlParameters(["energieklus", "projects", projectId, "create-visitation"]);
    req.setHeaders(CONTENT_TYPE.JSON);
    req.setRequestBody({
      visitee_id: visitee_id,
      visitee_name: visitee_name,
      visitee_email: visitee_email,
      visitee_phone: visitee_phone,
      timeslot: timeslot,
      send_confirmation_emails: send_confirmation_emails,
    });
    return this.apiHeatbrainService.post<IVisitation>(req, this.alert_on_error);
  }
}
